import { api } from './client'
import type { CoursePricingModel } from './admin'

export type PortalBrandingDto = {
  displayName: string | null
  logoUrl: string | null
  primaryColour: string | null
  secondaryColour: string | null
  headerStyle: string
  cornerStyle: string
  fontFamily: string
  currency: string
  region: string
  timeZoneId: string
}

export type PortalTermsStatusDto = {
  termsRequired: boolean
  termsVersion: number
  termsUpdatedAt: string | null
  acceptedVersion: number | null
  acceptedAt: string | null
}

export type TeamTrainingRecordDto = {
  id: string
  courseId: string
  courseName: string
  completedDate: string
  expiryDate: string | null
  status: 'Valid' | 'DueSoon' | 'Overdue' | 'NoExpiry'
  hasCertificate: boolean
  courseIsThirdPartyCertified: boolean
  certificateId: string | null
}

export type TeamMemberRecordsDto = {
  staffMemberId: string
  fullName: string
  email: string
  records: TeamTrainingRecordDto[]
}

// Dashboard
export type PortalAlertDto = {
  staffMemberId: string
  staffName: string
  courseName: string
  expiryDate: string | null
  status: 'Overdue' | 'DueSoon'
}

export type PortalUpcomingBookingDto = {
  id: string
  bookingDate: string
  courseName: string
  courseCode: string | null
  places: number
  status: string
}

export type PortalRecentCompletedBookingDto = {
  id: string
  bookingDate: string
  courseName: string
  places: number
  hasFeedback: boolean
  feedbackRating: number | null
}

export type PortalDashboardDto = {
  overdueCount: number
  expiringSoonCount: number
  upcomingBookingsCount: number
  outstandingInvoiceAmount: number
  alerts: PortalAlertDto[]
  upcomingBookings: PortalUpcomingBookingDto[]
  recentCompletedBookings: PortalRecentCompletedBookingDto[]
}

// Compliance
export type PortalCourseDto = {
  id: string
  name: string
  certifiedCount: number
  totalStaff: number
}

export type PortalStaffCourseStatusDto = {
  courseId: string
  status: 'Valid' | 'DueSoon' | 'Overdue' | 'NoExpiry' | 'NotCertified'
  expiryDate: string | null
  trainingRecordId: string | null
  hasCertificate: boolean
}

export type PortalStaffComplianceDto = {
  staffMemberId: string
  fullName: string
  courseStatuses: PortalStaffCourseStatusDto[]
}

export type PortalComplianceDto = {
  courses: PortalCourseDto[]
  staff: PortalStaffComplianceDto[]
}

// Bookings
export type PortalBookingDto = {
  id: string
  bookingDate: string
  courseName: string
  courseCode: string | null
  places: number
  status: string
  notes: string | null
  invoiceNumber: string | null
  invoiceAmount: number | null
  paymentStatus: string | null
  hasFeedback: boolean
  feedbackRating: number | null
}

export type PortalBookingFeedbackDto = {
  id: string
  rating: number
  comment: string | null
  submittedAt: string
  updatedAt: string | null
}

export type PortalBookingsDto = {
  upcoming: PortalBookingDto[]
  past: PortalBookingDto[]
}

// Invoices
export type PortalInvoiceDto = {
  id: string
  invoiceNumber: string
  issuedDate: string
  dueDate: string | null
  amount: number
  paymentStatus: string
  /** "First Aid" for a single-booking invoice, "First Aid +2 more" for a bundle. */
  courseSummary: string | null
  bookingDate: string | null
  notes: string | null
  currency: string
}

/** What the tenant lets this client do in the portal (tenant default + per-client override). */
export type PortalPermissions = {
  feedback: boolean
  staffManagement: boolean
  bookingRequests: boolean
  invoices: boolean
  editDetails: boolean
  assignAttendees: boolean
  bookSessions: boolean
}

// Open sessions: the client books its own staff onto the training company's public sessions
export type PortalSessionDto = {
  id: string
  courseId: string
  courseName: string
  courseDescription: string | null
  venueName: string | null
  venueCity: string | null
  deliveryMethod: 'InPerson' | 'Online' | string
  startsAt: string
  endsAt: string | null
  additionalDays: { startsAt: string; endsAt: string | null }[]
  totalPlaces: number
  availablePlaces: number
  /** Null or 0 means free. */
  pricePerPerson: number | null
  /** This client's staff already booked on the session, however they were booked. */
  bookedStaffIds: string[]
}

export type PortalSessionsDto = {
  /** Booking a priced session sends them to pay by card straight away. */
  requiresPaymentToBook: boolean
  sessions: PortalSessionDto[]
}

export type PortalSessionBookingResult = { bookingId: string; checkoutUrl: string | null }

export type PortalSessionBookingDto = {
  bookingId: string
  sessionId: string
  courseName: string
  startsAt: string
  venueName: string | null
  deliveryMethod: string
  status: 'Confirmed' | 'AwaitingPayment' | 'Cancelled' | string
  places: number
  total: number | null
  isPaid: boolean
  bookedAt: string
  attendees: { name: string; attendanceStatus: string }[]
  /** Where to finish paying, for a booking that is awaiting payment. */
  paymentPath: string | null
}

// Staff management
export type PortalStaffMemberDto = {
  id: string
  firstName: string
  lastName: string
  email: string
  role: string
  isActive: boolean
  invitePending: boolean
  photoUpdatedAt: string | null
}

export type PhotoSettings = { enabled: boolean; notice: string | null; showOnVerification: boolean }

export type PortalImportResult = {
  succeeded: number
  failed: number
  errors: string[]
}

export type PortalInvoiceLineDto = {
  bookingId: string
  bookingDate: string
  courseName: string
  courseCode: string | null
  places: number
  pricingModel: CoursePricingModel
  unitPrice: number | null
  flatFeeCapacity: number | null
  amount: number
}

// Invoice detail (full view)
export type PortalInvoiceDetailDto = {
  id: string
  invoiceNumber: string
  issuedDate: string
  dueDate: string | null
  amount: number
  vatRate: number | null
  vatAmount: number | null
  total: number
  paymentStatus: string
  notes: string | null
  lines: PortalInvoiceLineDto[]
  clientId: string
  clientName: string
  clientContactName: string | null
  clientContactEmail: string
  clientAddressLine1: string | null
  clientAddressLine2: string | null
  clientCity: string | null
  clientCounty: string | null
  clientPostcode: string | null
  clientCountry: string | null
  companyDisplayName: string | null
  companyLogoUrl: string | null
  companyAddressLine1: string | null
  companyAddressLine2: string | null
  companyCity: string | null
  companyCounty: string | null
  companyPostcode: string | null
  companyCountry: string | null
  companyEmail: string | null
  companyWebsite: string | null
  companyVatNumber: string | null
  companyNumber: string | null
  companyBankName: string | null
  companyAccountName: string | null
  companyAccountNumber: string | null
  companySortCode: string | null
  companyIban: string | null
  companyPaymentReference: string | null
  companyPaymentLink: string | null
  stripeConnectEnabled: boolean
  currency: string
  taxLabel: string
  companyRoutingNumber: string | null
  region: string
}

// Client profile
export type PortalClientProfileDto = {
  name: string
  contactName: string | null
  contactEmail: string
  phone: string | null
  addressLine1: string | null
  addressLine2: string | null
  city: string | null
  county: string | null
  postcode: string | null
  country: string | null
}

// Booking attendees
export type PortalBookingAttendeeDto = {
  staffMemberId: string
  firstName: string
  lastName: string
}

// Courses (for enquiry form)
export type PortalCourseListItemDto = {
  id: string
  name: string
  code: string | null
}

// Portal enquiry (client's own view)
export type PortalEnquiryDto = {
  id: string
  courseName: string
  preferredDate: string | null
  places: number
  message: string | null
  status: 'Pending' | 'Acknowledged' | 'Converted' | 'Declined'
  adminNotes: string | null
  createdAt: string
  requestedStaffNames: string[]
}

// Booking enquiry submission
export type SubmitEnquiryRequest = {
  courseId: string | null
  courseNameFreeText: string | null
  preferredDate: string | null
  places: number
  message: string | null
  staffMemberIds: string[]
}

export const portalApi = {
  // Staff management
  getSessions: () => api.get<PortalSessionsDto>('/portal/sessions'),
  bookSession: (sessionId: string, staffMemberIds: string[]) =>
    api.post<PortalSessionBookingResult>(`/portal/sessions/${sessionId}/bookings`, { staffMemberIds }),
  getSessionBookings: () => api.get<PortalSessionBookingDto[]>('/portal/session-bookings'),
  getStaff: () => api.get<PortalStaffMemberDto[]>('/portal/staff'),
  addStaff: (data: { email: string; firstName: string; lastName: string; role: string }) =>
    api.post<{ id: string }>('/portal/staff', data),
  resendStaffInvite: (id: string) => api.post(`/portal/staff/${id}/resend-invite`, {}),
  getPhotoSettings: () => api.get<PhotoSettings>('/portal/photo-settings'),
  getStaffPhoto: (id: string) => api.get<Blob>(`/portal/staff/${id}/photo`, { responseType: 'blob' }),
  setStaffPhoto: (id: string, photo: Blob) => { const f = new FormData(); f.append('file', photo, 'photo.jpg'); return api.post(`/portal/staff/${id}/photo`, f) },
  removeStaffPhoto: (id: string) => api.delete(`/portal/staff/${id}/photo`),
  updateStaff: (id: string, data: { email: string; firstName: string; lastName: string; role: string }) =>
    api.put(`/portal/staff/${id}`, data),
  importStaff: (file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api.post<PortalImportResult>('/portal/staff/import', form)
  },
  getBranding: () => api.get<PortalBrandingDto>('/portal/branding'),
  getPermissions: () => api.get<PortalPermissions>('/portal/permissions'),
  getTermsStatus: () => api.get<PortalTermsStatusDto>('/portal/terms-status'),
  acceptTerms: () => api.post('/portal/terms/accept'),
  getTermsDocument: () => api.get('/portal/terms', { responseType: 'blob' }),
  getEnquiries: () => api.get<PortalEnquiryDto[]>('/portal/booking-enquiries'),
  getCourses: () => api.get<PortalCourseListItemDto[]>('/portal/courses'),
  submitEnquiry: (body: SubmitEnquiryRequest) => api.post<{ id: string }>('/portal/booking-enquiries', body),
  getDashboard: () => api.get<PortalDashboardDto>('/portal/dashboard'),
  getCompliance: () => api.get<PortalComplianceDto>('/portal/compliance'),
  getProfile: () => api.get<PortalClientProfileDto>('/portal/profile'),
  updateProfile: (data: Omit<PortalClientProfileDto, 'name'>) => api.put('/portal/profile', data),
  getBookings: () => api.get<PortalBookingsDto>('/portal/bookings'),
  getBookingAttendees: (id: string) => api.get<PortalBookingAttendeeDto[]>(`/portal/bookings/${id}/attendees`),
  updateBookingAttendees: (id: string, staffMemberIds: string[]) =>
    api.put(`/portal/bookings/${id}/attendees`, { staffMemberIds }),
  getBookingFeedback: (id: string) => api.get<PortalBookingFeedbackDto>(`/portal/bookings/${id}/feedback`),
  submitFeedback: (id: string, data: { rating: number; comment: string | null }) =>
    api.post(`/portal/bookings/${id}/feedback`, data),
  getInvoices: () => api.get<PortalInvoiceDto[]>('/portal/invoices'),
  getInvoice: (id: string) => api.get<PortalInvoiceDetailDto>(`/portal/invoices/${id}`),
  getTeamRecords: () => api.get<TeamMemberRecordsDto[]>('/portal/team/training-records'),
  getTeamCertificateUrl: (trainingRecordId: string) =>
    api.get<{ url: string }>(`/portal/team/training-records/${trainingRecordId}/certificate`),
  streamTeamCertificate: (trainingRecordId: string) =>
    api.get(`/portal/team/training-records/${trainingRecordId}/certificate/stream`, { responseType: 'blob' }),
  downloadTeamCertificatesZip: (trainingRecordIds: string[]) =>
    api.post('/portal/team/training-records/certificates/zip', { trainingRecordIds }, { responseType: 'blob' }),

  // Notifications
  getNotifications: (limit = 50) => api.get<import('./admin').AppNotificationDto[]>(`/notifications?limit=${limit}`),
  getUnreadNotificationCount: () => api.get<number>('/notifications/unread-count'),
  markNotificationRead: (id: string) => api.post(`/notifications/${id}/read`, {}),
  markAllNotificationsRead: () => api.post('/notifications/read-all', {}),

  // Magic-link auth
  requestMagicLink: (email: string) =>
    api.post('/portal/auth/request-link', { email }),
  exchangeMagicLink: (token: string) =>
    api.post<{ accessToken: string; refreshToken: string }>('/portal/auth/exchange', { token }),

  // Invoice checkout (Stripe Connect)
  createInvoiceCheckout: (invoiceId: string) =>
    api.post<{ url: string }>(`/stripe-connect/invoices/${invoiceId}/checkout`, {}),
}
